import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Inbox } from "lucide-react";
import { listTasks } from "@/lib/afritask";
import TaskCard from "@/components/TaskCard";
import EnterRoundDialog from "@/components/EnterRoundDialog";

const TYPES = [
  { value: "all", label: "All types" },
  { value: "text_classification", label: "Text classification" },
  { value: "image_labeling", label: "Image labeling" },
  { value: "translation", label: "Translation" },
  { value: "audio_transcription", label: "Audio transcription" },
  { value: "prompt_rating", label: "Prompt rating" },
  { value: "voice_rating", label: "Voice rating" },
];

const LANGS = [
  { value: "all", label: "All languages" },
  { value: "Yoruba", label: "Yoruba" },
  { value: "Igbo", label: "Igbo" },
  { value: "Hausa", label: "Hausa" },
  { value: "Pidgin", label: "Pidgin" },
  { value: "English", label: "English" },
];

const SORTS = [
  { value: "reward", label: "Highest reward" },
  { value: "deadline", label: "Closing soonest" },
  { value: "popularity", label: "Most popular" },
];

export default function Tasks() {
  const { refreshWallet } = useOutletContext();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [language, setLanguage] = useState("all");
  const [sort, setSort] = useState("reward");
  const [q, setQ] = useState("");
  const [entering, setEntering] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (type !== "all") params.type = type;
      if (language !== "all") params.language = language;
      const list = await listTasks(params);
      setTasks(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, language, sort]);

  const visible = tasks.filter(
    (t) =>
      !q ||
      t.title.toLowerCase().includes(q.toLowerCase()) ||
      t.description.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl">
            Available tasks
          </h1>
        </div>
        <p className="text-sm text-neutral-500">
          <span
            className="font-semibold text-neutral-900 tabular-nums"
            data-testid="task-count"
          >
            {visible.length}
          </span>{" "}
          / {tasks.length} shown
        </p>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Search tasks…"
              className="pl-9"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="tasks-search"
            />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="sm:w-52" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="sm:w-44" data-testid="filter-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-44" data-testid="filter-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Grid */}
      {loading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-48 animate-pulse rounded-xl border border-neutral-200 bg-neutral-100"
            />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="rounded-full bg-neutral-100 p-4">
              <Inbox className="h-6 w-6 text-neutral-400" />
            </div>
            <h3 className="text-base font-semibold">No matches</h3>
            <p className="max-w-md text-sm text-neutral-500">
              Try widening your filters or searching for something else.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div
          className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
          data-testid="tasks-grid"
        >
          {visible.map((t) => (
            <TaskCard
              key={t.task_id}
              variant="available"
              task={t}
              onEnter={setEntering}
            />
          ))}
        </div>
      )}

      <EnterRoundDialog
        task={entering}
        onClose={() => setEntering(null)}
        onEntered={async () => {
          await Promise.all([load(), refreshWallet()]);
        }}
      />
    </div>
  );
}
